import {Modal} from 'antd';
import {useEffect, useRef, useState} from 'react';

import {Spinner} from '../../../layout/spinner/spinner';
import {Locale} from '../../../provider/locale/locale';
import {addCompaniesApi} from '../../../service/feature-companies/feature-companies-api';
import {postCompanySelectorCompanySchema} from '../../../service/feature-companies/feature-companies-api-type';
import {CompaniesSelectorFeatureEnum} from '../../../service/feature-companies/feature-companies-const';
import {useCompaniesSelector} from '../../../service/feature-companies/feature-companies-hook';
import {PostSelectorCompanyType} from '../../../service/feature-companies/feature-companies-type';
import {usePromotion, useUpdatePromotion} from '../../../service/promotions/promotions-hook';
import {CommonPromotionStatusEnum, PromotionFormType} from '../../../service/promotions/promotions-type';
import {noop} from '../../../util/function';

import {PromotionForm} from './promotion-form';
import {
    getPromotionFormInitialValues,
    preparePromotionRequestData,
    PROMOTION_SELECT_COMPANIES_STEP_WIDTH,
} from './promotion-form-helper';

type PropsType = {
    promotionId: number;
    setIsOpen: (isOpen: boolean) => void;
    onUpdateSuccess?: VoidFunction;
};

export function EditPromotionFormModal({promotionId, setIsOpen, onUpdateSuccess}: PropsType): JSX.Element {
    const [modalWidth, setModalWidth] = useState<number>(PROMOTION_SELECT_COMPANIES_STEP_WIDTH);

    const {data: promotion, isFetching} = usePromotion(promotionId, true);
    const {mutateAsync: updatePromotion} = useUpdatePromotion(promotionId);

    const companiesSelector = useCompaniesSelector<PostSelectorCompanyType>({
        feature: CompaniesSelectorFeatureEnum.Posts,
        companySchema: postCompanySelectorCompanySchema,
        resourceId: null,
        initialCompaniesCount: promotion?.companiesIds.length ?? 0,
    });

    const {initializeSelector, selectorId, setCompaniesSelectorFilter} = companiesSelector;

    useEffect(() => {
        initializeSelector();
    }, [initializeSelector]);

    // Seed the fresh posts selector with the promotion's existing companies, then reload to reflect them.
    const seededRef = useRef<boolean>(false);

    useEffect(() => {
        if (!promotion || !selectorId || seededRef.current) {
            return;
        }

        seededRef.current = true;

        if (promotion.companiesIds.length > 0) {
            addCompaniesApi({
                selectorId,
                body: {ids: promotion.companiesIds},
                feature: CompaniesSelectorFeatureEnum.Posts,
            })
                .then(() => setCompaniesSelectorFilter((previousFilter) => ({...previousFilter})))
                .catch(noop);
        }
    }, [promotion, selectorId, setCompaniesSelectorFilter]);

    async function handleSubmit(values: PromotionFormType, status: CommonPromotionStatusEnum): Promise<void> {
        await updatePromotion(preparePromotionRequestData(values, selectorId, status));
        onUpdateSuccess?.();
    }

    return (
        <Modal
            centered
            destroyOnClose
            footer={null}
            onCancel={() => setIsOpen(false)}
            open
            title={<Locale stringKey="PROMOTIONS__EDIT__TITLE" />}
            width={modalWidth}
        >
            {isFetching || !promotion ? (
                <Spinner />
            ) : (
                <PromotionForm
                    closeModal={() => setIsOpen(false)}
                    companiesSelector={companiesSelector}
                    initialValues={getPromotionFormInitialValues(promotion)}
                    mode="edit"
                    onChangeStep={setModalWidth}
                    onSubmit={handleSubmit}
                />
            )}
        </Modal>
    );
}
